<template>
  <AuthCard
      class="signup-page"
      :title="t('page.signup-title')"
      :subtitle="t('page.signup-subtitle')"
  >
    <template #header>
      <!-- Сначала логотип -->
      <div class="signup-page__logo">
        <img :src="logo" alt="logo" />
        <img :src="assistant" alt="assistant" />
      </div>

      <!-- Потом кнопка назад (под логотипом) -->
      <a-button
          class="signup-page__back-button"
          type="link"
          @click="handleBack"
      >
        <LeftOutlined /> {{ t('ui.back-button-label') }}
      </a-button>
    </template>

    <AuthForm
        class="signup-page__form"
        :model="form"
        :rules="rules"
        layout="vertical"
        @finish="handleFinish"
    >
      <a-form-item
          :label="t('ui.firstname-input-label')"
          name="first_name"
          :rules="rules.first_name"
      >
        <a-input
            v-model:value="form.first_name"
            :placeholder="t('ui.firstname-input-placeholder')"
            size="large"
            :disabled="loading"
        />
      </a-form-item>

      <a-form-item
          :label="t('ui.lastname-input-label')"
          name="last_name"
          :rules="rules.last_name"
      >
        <a-input
            v-model:value="form.last_name"
            :placeholder="t('ui.lastname-input-placeholder')"
            size="large"
            :disabled="loading"
        />
      </a-form-item>

      <a-form-item :label="t('ui.email-input-label')" name="email" :rules="rules.email">
        <a-input
            v-model:value="form.email"
            placeholder="email@email.com"
            size="large"
            :disabled="loading"
        />
      </a-form-item>

      <template #actions>
        <a-button
            class="signup-page__form-button full-width"
            type="primary"
            html-type="submit"
            :loading="loading"
            :disabled="!isFormValid || loading"
        >
          {{ t('ui.submit-button-label') }}
        </a-button>
      </template>
    </AuthForm>
  </AuthCard>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { AuthCard, AuthForm } from '@/ui/common/auth'
import type { Rule } from 'ant-design-vue/es/form'
import { Form } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import { LeftOutlined } from '@ant-design/icons-vue'

import logo from '@/assets/icons/menu/logo.svg'
import assistant from '@/assets/icons/menu/assistant.svg'
import type { SignUpPayload } from '@/api/modules/auth/types'
import { Routes } from '@/config/router/routes'

const { t } = useI18n()
const router = useRouter()

const props = defineProps<{
  loading: boolean
}>()

const emit = defineEmits<{
  (e: 'submit', formData: SignUpPayload): void
}>()

const form = reactive<SignUpPayload>({
  first_name: '',
  last_name: '',
  email: '',
})

const validateEmail = (_rule: any, value: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  if (!value) {
    return Promise.reject(t('rules.email-required-text'))
  }

  if (!emailRegex.test(value)) {
    return Promise.reject(t('rules.email-required-text'))
  }

  return Promise.resolve()
}

const rules = computed<Record<string, Rule[]>>(() => ({
  email: [
    {
      required: true,
      validator: validateEmail,
      trigger: 'change'
    }
  ],
  first_name: [
    {
      required: true,
      message: t('rules.firstname-required-text'),
      trigger: 'change'
    }
  ],
  last_name: [
    {
      required: true,
      message: t('rules.lastname-required-text'),
      trigger: 'change'
    }
  ],
}))

const { validate } = Form.useForm(form, rules)

const isFormValid = ref(false)

const handleFinish = async () => {
  try {
    await validate()
    emit('submit', { ...form })
  } catch (error) {
    console.error('Validation error:', error)
  }
}

const handleBack = () => {
  router.push(Routes.Login)
}

watch(
    () => form,
    async () => {
      try {
        await validate()
        isFormValid.value = true
      } catch {
        isFormValid.value = false
      }
    },
    { deep: true, immediate: true }
)
</script>

<style lang="scss" scoped>
.signup-page {
  &__logo {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    padding: 16px 24px;
    gap: 8px;
  }

  &__back-button {
    margin-right: auto;
    padding: 8px;
    margin-bottom: 8px;
  }

  &__form-button {
    width: 100%;
    margin-bottom: 8px;
  }
}
</style>